import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import GlowRing from '../../../components/ui/GlowRing';

const HEART_RATE_ZONES = [
  { id: 1, name: 'Rest', range: '< 100 bpm', color: '#94a3b8', min: 0, max: 100 },
  { id: 2, name: 'Fat Burn', range: '100–120 bpm', color: '#00D4FF', min: 100, max: 120 },
  { id: 3, name: 'Cardio', range: '120–150 bpm', color: '#AAFF00', min: 120, max: 150 },
  { id: 4, name: 'Peak', range: '150–170 bpm', color: '#FF6B00', min: 150, max: 170 },
  { id: 5, name: 'Max', range: '> 170 bpm', color: '#FF4444', min: 170, max: 220 },
];

const HeartRateZonePanel = ({ heartRate = 142, timeInZones = null }) => {
  const [animatedHR, setAnimatedHR] = useState(heartRate);
  const [pulse, setPulse] = useState(false);

  const defaultTimeInZones = timeInZones || { 1: 18, 2: 32, 3: 45, 4: 28, 5: 12 };
  const totalMinutes = Object.values(defaultTimeInZones)?.reduce((a, b) => a + b, 0);

  const currentZone = HEART_RATE_ZONES?.find(z => animatedHR >= z?.min && animatedHR < z?.max) || HEART_RATE_ZONES?.[2];

  useEffect(() => {
    const interval = setInterval(() => {
      setAnimatedHR(prev => {
        const delta = Math.floor(Math.random() * 7) - 3;
        return Math.max(55, Math.min(185, prev + delta));
      });
      setPulse(true);
      setTimeout(() => setPulse(false), 300);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const getZoneRingColor = (zone) => {
    if (zone?.id === 1) return 'cyan';
    if (zone?.id === 2) return 'cyan';
    if (zone?.id === 3) return 'lime';
    return 'orange';
  };

  return (
    <div className="glass-card p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${currentZone?.color}20` }}>
            <Icon name="Heart" size={18} color={currentZone?.color} />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-sm" style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>Heart Rate Zones</h3>
            <p className="text-xs text-muted-foreground">Live monitoring</p>
          </div>
        </div>
        <motion.div
          animate={{ scale: pulse ? 1.2 : 1 }}
          transition={{ duration: 0.15 }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold"
          style={{ backgroundColor: `${currentZone?.color}15`, color: currentZone?.color }}
        >
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: currentZone?.color }} />
          {currentZone?.name}
        </motion.div>
      </div>
      <div className="flex items-center gap-5 mb-5">
        <div className="flex-shrink-0">
          <GlowRing
            value={animatedHR}
            max={200}
            color={getZoneRingColor(currentZone)}
            size={128}
            strokeWidth={8}
            displayValue={animatedHR}
            label="Heart Rate"
            unit="bpm"
            sublabel="bpm"
          />
        </div>

        <div className="flex-1 space-y-2">
          {HEART_RATE_ZONES?.map((zone) => {
            const minutes = defaultTimeInZones?.[zone?.id] || 0;
            const pct = totalMinutes > 0 ? (minutes / totalMinutes) * 100 : 0;
            const isActive = zone?.id === currentZone?.id;
            return (
              <div key={zone?.id} className={`rounded-lg px-2.5 py-1.5 transition-all`}
                style={isActive ? { backgroundColor: `${zone?.color}10`, border: `1px solid ${zone?.color}40` } : {}}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium" style={{ color: isActive ? zone?.color : 'rgba(255,255,255,0.5)' }}>{zone?.name}</span>
                  <span className="text-xs font-bold font-metric" style={{ color: zone?.color }}>{minutes}m</span>
                </div>
                <div className="w-full rounded-full h-1" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div
                    className="h-1 rounded-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: zone?.color, boxShadow: `0 0 6px ${zone?.color}88` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default HeartRateZonePanel;
